import {
  toNumberOrZero,
  trimToEmpty,
} from "../portfolioReportPositions";
import { buildFundOverviewData } from "./fundOverviewData";
import {
  CREDIT_SUMMARY_RATING_ORDER,
  RATING_SECTION_LABELS,
  RATING_SECTION_ORDER,
  collapseLongTermRating,
  resolveReportedRating,
} from "pages/FundPlanner/lib/portfolioRatings";
import { roundToCents } from "pages/FundPlanner/lib/money";

export const MATURITY_BUCKETS = [
  { key: "overnight", label: "Overnight" },
  { key: "0_3mo", label: "0-3 Mo" },
  { key: "3_6mo", label: ">3-6 Mo" },
  { key: "6_12mo", label: ">6-12 Mo" },
  { key: "1_2yr", label: ">1-2 Yr" },
  { key: "2_3yr", label: ">2-3 Yr" },
  { key: "3_4yr", label: ">3-4 Yr" },
  { key: "4_5yr", label: ">4-5 Yr" },
  { key: "gt_5yr", label: ">5 Yr" },
];

export function getYieldToWorst(row) {
  const ytm = row?.yield_ytm_mid;
  const ytc = row?.yield_ytc_mid;

  if (ytm == null && ytc == null) return null;
  if (ytm == null) return ytc;
  if (ytc == null) return ytm;
  return Math.min(ytm, ytc);
}

export function getRatingBucket(value) {
  const s = trimToEmpty(value).toUpperCase();
  if (!s || s === "-" || s === "\u2014" || s === "NR" || s === "N/R" || s === "UNRATED") {
    return "Unrated";
  }

  if (s.startsWith("AAA")) return "AAA";
  if (s.startsWith("AA")) return "AA";
  if (s.startsWith("A")) return "A";
  if (s.startsWith("BBB")) return "BBB";
  if (
    s.startsWith("BB") ||
    s.startsWith("B") ||
    s.startsWith("CCC") ||
    s.startsWith("CC") ||
    s.startsWith("C") ||
    s.startsWith("D")
  ) {
    return "Below BBB";
  }

  return s;
}

export function getMaturityBucketKey(days) {
  if (days == null) return "gt_5yr";
  if (days <= 1) return "overnight";
  if (days <= 90) return "0_3mo";
  if (days <= 180) return "3_6mo";
  if (days <= 365) return "6_12mo";
  if (days <= 730) return "1_2yr";
  if (days <= 1095) return "2_3yr";
  if (days <= 1460) return "3_4yr";
  if (days <= 1825) return "4_5yr";
  return "gt_5yr";
}

export function buildOverviewMetrics(rows = []) {
  let totalPar = 0;
  let totalOriginalCost = 0;
  let totalBookValue = 0;
  let totalMarketValue = 0;
  let totalAccruedInterest = 0;
  let totalMarketValuePlusAccrued = 0;
  let totalUnrealizedGainLoss = 0;

  let ytwWeightedSum = 0;
  let ytwWeight = 0;

  let wamWeightedYearsSum = 0;
  let wamWeight = 0;

  for (const row of rows) {
    if (row?.is_pending_settlement) continue;
    const par = toNumberOrZero(row.current_amount ?? row.amount ?? row.par_amount);
    const originalCost = toNumberOrZero(row.original_cost);
    const bookValue = toNumberOrZero(row.book_value);
    const marketValue = toNumberOrZero(row.market_value);
    const accruedInterest = toNumberOrZero(row.accrued_interest);
    const marketValuePlusAccrued = toNumberOrZero(row.market_value_plus_accrued);
    const ugl = toNumberOrZero(row.unrealized_gain_loss);
    const ytw = getYieldToWorst(row);
    const years = row?.years_to_effective_maturity;

    totalPar += par;
    totalOriginalCost += originalCost;
    totalBookValue += bookValue;
    totalMarketValue += marketValue;
    totalAccruedInterest += accruedInterest;
    totalMarketValuePlusAccrued += marketValuePlusAccrued;
    totalUnrealizedGainLoss += ugl;

    if (ytw != null && marketValue > 0) {
      ytwWeightedSum += ytw * marketValue;
      ytwWeight += marketValue;
    }

    if (years != null && par > 0) {
      wamWeightedYearsSum += years * par;
      wamWeight += par;
    }
  }

  return {
    totalPar,
    totalOriginalCost,
    totalBookValue,
    totalMarketValue,
    totalAccruedInterest,
    totalMarketValuePlusAccrued,
    totalUnrealizedGainLoss,
    avgYieldToWorst: ytwWeight > 0 ? ytwWeightedSum / ytwWeight : null,
    wamYears: wamWeight > 0 ? wamWeightedYearsSum / wamWeight : null,
  };
}

export function getReportOverviewMetrics(metrics = {}) {
  return metrics;
}

export const PERIOD_PORTFOLIO_METRICS = [
  { key: "totalPar", label: "Par/Face Amount", format: "currency" },
  { key: "totalMarketValue", label: "Market Value", format: "currency" },
  { key: "totalBookValue", label: "Book Value", format: "currency" },
  { key: "totalAccruedInterest", label: "Accrued Interest", format: "currency" },
  { key: "totalMarketValuePlusAccrued", label: "Market Value + Accrued", format: "currency" },
  { key: "avgYieldToWorst", label: "Effective Yield", format: "percent" },
  { key: "wamYears", label: "Weighted Avg Maturity", format: "years" },
];

export const PERIOD_ACTIVITY_METRICS = [
  { key: "maturities", label: "Maturities / Principal Paydowns" },
  { key: "couponIncome", label: "Interest Income" },
  { key: "dividendIncome", label: "Dividend Income", omitWhenZero: true },
  { key: "amortizationAccretion", label: "Net Amortization/Accretion" },
  { key: "realizedGainLoss", label: "Realized Gain/Loss" },
  { key: "feeExpenses", label: "Fee Expenses", multiplier: -1 },
  { key: "netIncome", label: "Net Income", emphasis: true },
];

function enrichOverviewMetrics(metrics = {}) {
  const totalBookValue = toNumberOrZero(metrics.totalBookValue);
  const totalAccruedInterest = toNumberOrZero(metrics.totalAccruedInterest);

  return {
    ...metrics,
    totalBookValuePlusAccrued: totalBookValue + totalAccruedInterest,
  };
}

function getMetricChange(beginning, ending) {
  if (beginning == null || ending == null) return null;

  const beginningNumber = Number(beginning);
  const endingNumber = Number(ending);

  if (!Number.isFinite(beginningNumber) || !Number.isFinite(endingNumber)) {
    return null;
  }

  return endingNumber - beginningNumber;
}

export function buildPeriodOverviewData(periodAnalysis = null) {
  if (!periodAnalysis) return null;

  const beginningMetrics = enrichOverviewMetrics(
    buildOverviewMetrics(periodAnalysis.beginningRows || [])
  );
  const endingMetrics = enrichOverviewMetrics(
    buildOverviewMetrics(periodAnalysis.endingRows || [])
  );
  const comparisonMetrics = enrichOverviewMetrics(
    buildOverviewMetrics(periodAnalysis.comparisonRows || [])
  );

  const portfolioRows = PERIOD_PORTFOLIO_METRICS.map((metric) => {
    const beginning = metric.key === "avgYieldToWorst"
      ? comparisonMetrics[metric.key]
      : beginningMetrics[metric.key];
    const ending = endingMetrics[metric.key];

    return {
      key: metric.key,
      label: metric.label,
      format: metric.format,
      beginning,
      ending,
      change: getMetricChange(beginning, ending),
    };
  });

  const summary = periodAnalysis.summary || {};
  const couponIncome = roundToCents(toNumberOrZero(summary.couponIncome));
  const dividendIncome = roundToCents(toNumberOrZero(summary.dividendIncome));
  const amortizationAccretion = roundToCents(
    toNumberOrZero(summary.amortizationAccretion)
  );
  const realizedGainLoss = roundToCents(toNumberOrZero(summary.realizedGainLoss));
  const feeExpenses = roundToCents(toNumberOrZero(summary.feeExpenses));
  const netIncome = roundToCents(
    couponIncome +
      dividendIncome +
      amortizationAccretion +
      realizedGainLoss -
      feeExpenses
  );
  const activitySummary = {
    ...summary,
    couponIncome,
    dividendIncome,
    amortizationAccretion,
    realizedGainLoss,
    feeExpenses,
    netIncome,
  };

  const activityRows = PERIOD_ACTIVITY_METRICS
    .filter(
      (metric) =>
        !metric.omitWhenZero || toNumberOrZero(activitySummary[metric.key]) !== 0
    )
    .map((metric) => ({
      key: metric.key,
      label: metric.label,
      value: toNumberOrZero(activitySummary[metric.key]) * (metric.multiplier || 1),
      emphasis: Boolean(metric.emphasis),
    }));

  return {
    startDate: periodAnalysis.startDate,
    endDate: periodAnalysis.endDate,
    comparisonPeriod: periodAnalysis.comparisonPeriod || null,
    beginningMetrics,
    endingMetrics,
    portfolioRows,
    activityRows,
    summary: activitySummary,
    beginningPositionCount: periodAnalysis.beginningRows?.length || 0,
    endingPositionCount: periodAnalysis.endingRows?.length || 0,
    activityCount: periodAnalysis.activities?.length || 0,
  };
}

export function buildMaturityBuckets(rows = []) {
  const seed = MATURITY_BUCKETS.map((bucket) => ({
    ...bucket,
    marketValue: 0,
    parAmount: 0,
    count: 0,
    percentOfTotal: 0,
  }));

  const map = new Map(seed.map((bucket) => [bucket.key, bucket]));
  let totalMarketValue = 0;

  for (const row of rows) {
    if (row?.is_pending_settlement || row?.is_activity_only) continue;
    const key = getMaturityBucketKey(row?.days_to_effective_maturity);
    const bucket = map.get(key);
    const marketValue = toNumberOrZero(row.market_value);
    const par = toNumberOrZero(row.par_amount);

    bucket.marketValue += marketValue;
    bucket.parAmount += par;
    bucket.count += 1;
    totalMarketValue += marketValue;
  }

  for (const bucket of seed) {
    bucket.percentOfTotal =
      totalMarketValue > 0 ? bucket.marketValue / totalMarketValue : 0;
  }

  return {
    buckets: seed,
    totalMarketValue,
  };
}

export function buildAllocation(rows = [], getKey, preferredOrder = []) {
  const map = new Map();
  let totalMarketValue = 0;

  for (const row of rows) {
    if (row?.is_pending_settlement) continue;
    const key = getKey(row);
    const marketValue = toNumberOrZero(row.market_value);

    if (!map.has(key)) {
      map.set(key, {
        label: key,
        marketValue: 0,
        count: 0,
        percentOfTotal: 0,
      });
    }

    const item = map.get(key);
    item.marketValue += marketValue;
    item.count += 1;
    totalMarketValue += marketValue;
  }

  let items = [...map.values()];

  if (preferredOrder.length) {
    const orderMap = new Map(preferredOrder.map((label, index) => [label, index]));
    items.sort((a, b) => {
      const aIdx = orderMap.has(a.label) ? orderMap.get(a.label) : 999;
      const bIdx = orderMap.has(b.label) ? orderMap.get(b.label) : 999;
      if (aIdx !== bIdx) return aIdx - bIdx;
      return String(a.label).localeCompare(String(b.label));
    });
  } else {
    items.sort((a, b) => b.marketValue - a.marketValue || a.label.localeCompare(b.label));
  }

  items = items
    .filter((item) => Math.abs(item.marketValue) >= 0.005)
    .map((item) => ({
      ...item,
      percentOfTotal: totalMarketValue > 0 ? item.marketValue / totalMarketValue : 0,
    }));

  return {
    items,
    totalMarketValue,
  };
}

export function buildCreditRatingSummary(rows = []) {
  const sectionMaps = new Map(RATING_SECTION_ORDER.map((key) => [key, new Map()]));
  const combinedMap = new Map();
  let portfolioMarketValue = 0;

  for (const row of rows) {
    if (row?.is_pending_settlement) continue;
    const marketValue = toNumberOrZero(row?.market_value);
    if (Math.abs(marketValue) < 0.005) continue;

    const classification = resolveReportedRating(row);
    const sectionKey = classification.section;
    const normalizedRating = classification.section === "long_term"
      ? collapseLongTermRating(classification.normalizedRating)
      : classification.normalizedRating;
    const detailLabel = sectionKey === "secured_deposit"
      ? trimToEmpty(row?.investment_type) || "Secured Deposit"
      : normalizedRating;
    const sectionMap = sectionMaps.get(sectionKey) || sectionMaps.get("unrated");

    if (!sectionMap.has(detailLabel)) {
      sectionMap.set(detailLabel, { label: detailLabel, marketValue: 0, count: 0 });
    }
    sectionMap.get(detailLabel).marketValue += marketValue;
    sectionMap.get(detailLabel).count += 1;

    if (!combinedMap.has(normalizedRating)) {
      combinedMap.set(normalizedRating, {
        label: normalizedRating,
        marketValue: 0,
        count: 0,
      });
    }
    const combined = combinedMap.get(normalizedRating);
    combined.marketValue += marketValue;
    combined.count += 1;
    portfolioMarketValue += marketValue;
  }

  const withPercent = (item) => ({
    ...item,
    percentOfTotal: portfolioMarketValue > 0 ? item.marketValue / portfolioMarketValue : 0,
  });
  const ratingOrder = new Map(
    CREDIT_SUMMARY_RATING_ORDER.map((label, index) => [label, index])
  );
  const sections = RATING_SECTION_ORDER.map((key) => {
    const items = [...sectionMaps.get(key).values()]
      .sort((a, b) => {
        const aIndex = ratingOrder.get(a.label) ?? Number.MAX_SAFE_INTEGER;
        const bIndex = ratingOrder.get(b.label) ?? Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex || a.label.localeCompare(b.label);
      })
      .map(withPercent);
    const totalMarketValue = items.reduce((sum, item) => sum + item.marketValue, 0);
    return {
      key,
      label: RATING_SECTION_LABELS[key],
      items,
      totalMarketValue,
      percentOfTotal: portfolioMarketValue > 0 ? totalMarketValue / portfolioMarketValue : 0,
    };
  }).filter((section) => section.items.length > 0);

  const combinedAllocation = [...combinedMap.values()]
    .sort((a, b) => {
      const aIndex = ratingOrder.get(a.label) ?? Number.MAX_SAFE_INTEGER;
      const bIndex = ratingOrder.get(b.label) ?? Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex || a.label.localeCompare(b.label);
    })
    .map(withPercent);

  return { sections, combinedAllocation, portfolioMarketValue };
}

export function buildTopHoldings(rows = [], topN = 10) {
  const map = new Map();
  let totalMarketValue = 0;

  for (const row of rows) {
    if (row?.is_pending_settlement) continue;
    const key = trimToEmpty(row.cusip_or_id) || "\u2014";
    const marketValue = toNumberOrZero(row.market_value);
    const par = toNumberOrZero(row.par_amount);
    totalMarketValue += marketValue;

    if (!map.has(key)) {
      map.set(key, {
        cusip_or_id: key,
        issuer_name: trimToEmpty(row.issuer_name) || "\u2014",
        investment_type: trimToEmpty(row.investment_type) || "\u2014",
        rating_rollup: trimToEmpty(row.rating_rollup) || "\u2014",
        effective_maturity_date: trimToEmpty(row.effective_maturity_date) || "",
        par_amount: 0,
        market_value: 0,
      });
    }

    const item = map.get(key);
    item.par_amount += par;
    item.market_value += marketValue;

    const candidateMaturity = trimToEmpty(row.effective_maturity_date);
    if (
      candidateMaturity &&
      (!item.effective_maturity_date || candidateMaturity < item.effective_maturity_date)
    ) {
      item.effective_maturity_date = candidateMaturity;
    }
  }

  return [...map.values()]
    .sort((a, b) => b.market_value - a.market_value || a.cusip_or_id.localeCompare(b.cusip_or_id))
    .slice(0, topN)
    .map((item) => ({
      ...item,
      percentOfTotal: totalMarketValue > 0 ? item.market_value / totalMarketValue : 0,
    }));
}

export function buildPortfolioMainReportData({
  rows = [],
  funds = [],
  positions = [],
  asOfDate = "",
  reportMode = "single",
  startDate = "",
  endDate = "",
  periodAnalysis = null,
  orgName = "",
  portfolioName = "",
  reportTitle = "FundPlanner - Portfolio Overview",
} = {}) {
  const snapshotOverviewMetrics = buildOverviewMetrics(rows);
  const periodOverview = reportMode === "period"
    ? buildPeriodOverviewData(periodAnalysis)
    : null;
  const overviewMetrics = getReportOverviewMetrics(
    snapshotOverviewMetrics,
    reportMode,
    periodOverview
  );
  const fundOverview = buildFundOverviewData({
    funds,
    positions,
    rows,
    reportMode,
    periodAnalysis,
  });
  const maturityData = buildMaturityBuckets(rows);
  const allocationByType = buildAllocation(
    rows,
    (row) => trimToEmpty(row.investment_type) || "Unknown"
  );
  const creditRatingSummary = buildCreditRatingSummary(rows);
  const topHoldings = buildTopHoldings(rows, 10);

  return {
    meta: {
      reportTitle,
      reportMode,
      orgName,
      portfolioName,
      asOfDate,
      startDate,
      endDate,
      asOfLabel: asOfDate ? `As of ${asOfDate}` : "",
      fileDate: endDate || asOfDate || new Date(),
      positionCount: rows.filter((row) => !row?.is_pending_settlement).length,
      pendingSettlementCount: rows.filter((row) => row?.is_pending_settlement).length,
    },
    overviewMetrics,
    periodOverview,
    fundOverview,
    maturityData,
    allocationByType,
    allocationByRating: {
      items: creditRatingSummary.combinedAllocation,
      totalMarketValue: creditRatingSummary.portfolioMarketValue,
    },
    creditRatingSummary,
    topHoldings,
  };
}
